import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Chip, Button } from '@coordio/ui';
import { memoryService } from '../../services/memoryService';
import { useNotificationStore } from '../../store/notificationStore';
import { MemoryType, type AgentMemoryEntryDto } from '../../types/memory';

const typeLabel = (type: number) => {
  switch (type) {
    case MemoryType.Fact:
      return 'Fact';
    case MemoryType.Decision:
      return 'Decision';
    case MemoryType.Outcome:
      return 'Outcome';
    case MemoryType.Preference:
      return 'Preference';
    default:
      return 'Unknown';
  }
};

/**
 * Compact memory list component for a session sidebar.
 */
export const SessionMemoryList = ({ sessionId }: { sessionId: string }) => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [memories, setMemories] = useState<AgentMemoryEntryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const result = await memoryService.getSessionMemories(sessionId, {
        query: query || undefined,
        useSemanticSearch: true,
        limit: 10,
      });
      setMemories(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load memories';
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, query]);

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Memories</span>
        <Button size="sm" variant="ghost" onClick={() => navigate(`/memories?sessionId=${sessionId}`)}>
          View all
        </Button>
      </div>

      <TextField
        size="small"
        placeholder="Search memories..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mt: 1, mb: 1 }}
        fullWidth
      />

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <List dense disablePadding>
          {memories.length === 0 ? (
            <div className="px-2 py-2">
              <p className="text-sm text-muted-foreground">
                No memories yet.
              </p>
            </div>
          ) : (
            memories.map((m) => (
              <ListItemButton key={m.id} onClick={() => navigate(`/memories/${m.id}`)}>
                <ListItemText
                  primary={m.summary || (m.content.length > 60 ? `${m.content.slice(0, 60)}…` : m.content)}
                  secondary={
                    <span>
                      <Chip
                        label={typeLabel(m.type)}
                        variant="outlined"
                      />
                      <span className="ml-1">{m.importanceScore.toFixed(2)}</span>
                    </span>
                  }
                />
              </ListItemButton>
            ))
          )}
        </List>
      )}
    </div>
  );
};
